import { useId } from "react";
import { Form, useSubmit } from "react-router";
import { trackEvent } from "#app/utils/gtags.client.ts";
import { Button } from "../ui/button.tsx";
import { Input } from "../ui/input.tsx";
import { Label } from "../ui/label.tsx";

type Props = {
  words: { slug: string; term: string }[];
  value: string;
  error?: string;
};

/**
 * A GET form, so it works without JavaScript (EC-6) and the URL stays shareable. The suggestions are a native
 * <datalist> (decision 3). With JavaScript, picking a suggestion submits straight away.
 */
export function WordPicker({ words, value, error }: Props) {
  const id = useId();
  const listId = `${id}-words`;
  const errorId = `${id}-error`;
  const submit = useSubmit();
  const terms = new Map(words.map((w) => [w.term.toLowerCase(), w]));

  return (
    <Form method="get" action="/wear" preventScrollReset className="space-y-2">
      <Label htmlFor={id} className="font-sans">
        Your word
      </Label>
      <div className="flex gap-2">
        <Input
          key={value}
          id={id}
          name="word"
          list={listId}
          defaultValue={value}
          autoComplete="off"
          placeholder="Start typing a word"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className="font-sans"
          onChange={(e) => {
            const word = terms.get(e.currentTarget.value.trim().toLowerCase());
            if (!word) return;
            trackEvent("wear_word_select", { word: word.slug });
            void submit(e.currentTarget.form, { preventScrollReset: true });
          }}
        />
        <Button
          type="submit"
          variant="outline"
          className="shrink-0 whitespace-nowrap"
        >
          Show it
        </Button>
      </div>
      <datalist id={listId}>
        {words.map((w) => (
          <option key={w.slug} value={w.term} />
        ))}
      </datalist>
      {error ? (
        <p id={errorId} className="text-body-xs text-foreground-destructive">
          {error}
        </p>
      ) : (
        <p className="text-body-xs text-muted-foreground">
          Any word from the dictionary with 12 letters or fewer. Spaces, hyphens
          and apostrophes are printed as they are.
        </p>
      )}
    </Form>
  );
}
